// A Properties project: 1 the model (an SKFitter fit, Wannier90 files or a model file, optionally modified - the
// model step of NEGF Transport), 2 the properties of that model (prayog_properties calculators).
import { Alert, Card, Loader, Stepper } from "@mantine/core";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useRef, useState } from "react";
import { patch, type ProjectDetail } from "../../lib/api";
import { useMe } from "../../lib/hooks";
import { defaultPropertiesMemory, type PropertiesMemory } from "../../lib/properties";
import { ModelStep } from "../negf/ModelStep";
import { PropertiesStep } from "./PropertiesStep";
import { PropertiesExampleBanner } from "../PropertiesExamples";
import { PresetModifiers } from "./PresetModifiers";

export function PropertiesWorkspace({ project }: { project: ProjectDetail }) {
  const { data: me } = useMe();
  const qc = useQueryClient();
  const saved = project.settings?.properties as Partial<PropertiesMemory> | undefined;
  const [mem, setMem] = useState<PropertiesMemory>(() => ({ ...defaultPropertiesMemory(), ...(saved ?? {}) }));
  const [step, setStep] = useState<number>(saved?.model_file ? 1 : 0);
  const latest = useRef(mem);
  latest.current = mem;
  const remember = useCallback((next: Partial<PropertiesMemory>) => {
    const merged = { ...latest.current, ...next };
    latest.current = merged;
    setMem(merged);
    patch(`/projects/${project.id}`, { settings: { ...(project.settings ?? {}), properties: merged } })
      .then(() => qc.invalidateQueries({ queryKey: ["project", project.id] }))
      .catch(() => undefined);
  }, [project.id, project.settings, qc]);
  const update = useCallback((next: Partial<PropertiesMemory>) => setMem((m) => {
    const merged = { ...m, ...next };
    latest.current = merged;
    return merged;
  }), []);

  if (!me) return <Card withBorder><Loader size="sm" /></Card>;
  const limits = project.limits ?? me.limits;
  if (!limits["properties.enabled"])
    return <Alert color="orange" title="Not included in your plan">Properties is not part of your plan.</Alert>;
  const s = mem.summary;
  return (
    <>
    <PropertiesExampleBanner project={project} mem={mem} />
    <PresetModifiers project={project} mem={mem} remember={remember} />
    <Card withBorder>
      <Stepper active={step} onStepClick={setStep} size="sm" allowNextStepsSelect={false}>
        <Stepper.Step label="Model" description={s ? `${s.formula} · ${s.orbitals_per_cell} orbitals / cell` : "fit, Wannier90 or file"}>
          <ModelStep project={project} mem={mem} remember={remember as any} update={update as any} limits={limits}
            product="properties" nextLabel="Next: properties" onNext={() => setStep(1)} />
        </Stepper.Step>
        <Stepper.Step label="Properties" description="electronic structure, Berry phase and topology" disabled={!mem.model_file}>
          {mem.model_file && <PropertiesStep project={project} mem={mem} update={update} remember={remember} onBack={() => setStep(0)} />}
        </Stepper.Step>
      </Stepper>
    </Card>
    </>
  );
}
